import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SCRIPT_ID = "recaptcha-api-script";
const ONLOAD_CALLBACK = "__onRecaptchaApiLoad";

// Loads the reCAPTCHA v2 API once per page and resolves when grecaptcha.render
// is usable. Explicit render mode so the widget mounts into a React-owned node
// instead of scanning the DOM for .g-recaptcha on script load.
let apiPromise = null;
const loadRecaptchaApi = () => {
  if (window.grecaptcha?.render) return Promise.resolve(window.grecaptcha);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise((resolve, reject) => {
    window[ONLOAD_CALLBACK] = () => resolve(window.grecaptcha);
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?onload=${ONLOAD_CALLBACK}&render=explicit`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      apiPromise = null;
      script.remove();
      reject(new Error("Failed to load reCAPTCHA"));
    };
    document.head.appendChild(script);
  });
  return apiPromise;
};

/**
 * reCAPTCHA v2 checkbox. Reports the token through onChange (null when it
 * expires or errors). The parent can call ref.reset() — tokens are single-use,
 * so reset after every login attempt the server has consumed.
 */
const RecaptchaCheckbox = forwardRef(({ siteKey, onChange, onLoadError }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null && window.grecaptcha) {
        window.grecaptcha.reset(widgetIdRef.current);
      }
      onChangeRef.current?.(null);
    },
  }));

  useEffect(() => {
    let cancelled = false;
    loadRecaptchaApi()
      .then((grecaptcha) => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
        widgetIdRef.current = grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onChangeRef.current?.(token),
          "expired-callback": () => onChangeRef.current?.(null),
          "error-callback": () => onChangeRef.current?.(null),
        });
      })
      .catch((error) => {
        console.error("reCAPTCHA failed to load:", error);
        if (!cancelled) onLoadError?.(error);
      });
    return () => {
      cancelled = true;
    };
  }, [siteKey, onLoadError]);

  return <div ref={containerRef} />;
});

export default RecaptchaCheckbox;
